"use client"

import { useSyncExternalStore } from "react"
import type { PartnerHospitalSchedule } from "@/entities/hospital/api/types"

export type HospitalOpenState = "unknown" | "open" | "closed"

const koreaClock = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul", weekday: "long", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
})
const dayNames = [
  ["일", "일요일", "Sunday", "SUNDAY", "日曜日", "星期日"],
  ["월", "월요일", "Monday", "MONDAY", "月曜日", "星期一"],
  ["화", "화요일", "Tuesday", "TUESDAY", "火曜日", "星期二"],
  ["수", "수요일", "Wednesday", "WEDNESDAY", "水曜日", "星期三"],
  ["목", "목요일", "Thursday", "THURSDAY", "木曜日", "星期四"],
  ["금", "금요일", "Friday", "FRIDAY", "金曜日", "星期五"],
  ["토", "토요일", "Saturday", "SATURDAY", "土曜日", "星期六"],
]
const dayIndex = (day: string) => dayNames.findIndex((names) => names.includes(day))
const minutes = (time: string) => {
  const match = /^(\d{2}):(\d{2})$/.exec(time)
  if (!match || +match[1] > 23 || +match[2] > 59) return Number.NaN
  return +match[1] * 60 + +match[2]
}

/** 캐시된 응답의 요일이나 방문자 시간대가 한국 병원의 오늘 진료를 바꾸지 않게 한다. */
export const getHospitalSchedule = (
  weeklySchedules: PartnerHospitalSchedule[],
  todaySchedule: PartnerHospitalSchedule | undefined,
  now: Date,
): { schedule?: PartnerHospitalSchedule; openState: HospitalOpenState } => {
  const parts = koreaClock.formatToParts(now)
  const part = (type: string) => parts.find((value) => value.type === type)?.value ?? ""
  const today = dayIndex(part("weekday"))
  // 서버가 오늘 요일에 맞는 별도 시간표를 주었다면 주간 표보다 우선한다.
  const schedule = todaySchedule && dayIndex(todaySchedule.dayType) === today
    ? todaySchedule
    : weeklySchedules.find((value) => dayIndex(value.dayType) === today)
  if (!schedule) return { openState: "closed" }

  const start = minutes(schedule.startTime)
  const end = minutes(schedule.endTime)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return { schedule, openState: "unknown" }
  }
  const current = +part("hour") * 60 + +part("minute")
  return { schedule, openState: current >= start && current < end ? "open" : "closed" }
}

const getMinute = () => Math.floor(Date.now() / 60_000)
const getServerMinute = () => null
const subscribeMinute = (onChange: () => void) => {
  let interval: ReturnType<typeof setInterval> | undefined
  const timeout = setTimeout(() => {
    onChange()
    interval = setInterval(onChange, 60_000)
  }, 60_000 - Date.now() % 60_000)
  return () => { clearTimeout(timeout); clearInterval(interval) }
}

/** 분 경계와 자정에 시간표도 다시 선택한다. SSR에는 날짜를 추측한 배지를 그리지 않는다. */
export const useHospitalSchedule = (
  weeklySchedules: PartnerHospitalSchedule[],
  todaySchedule?: PartnerHospitalSchedule,
) => {
  const minute = useSyncExternalStore(subscribeMinute, getMinute, getServerMinute)
  return minute === null
    ? { schedule: undefined, openState: "unknown" as HospitalOpenState }
    : getHospitalSchedule(weeklySchedules, todaySchedule, new Date(minute * 60_000))
}
